import $ from "jquery";
import { createCalendar, serializeSelectedSet } from "./core.js";
import { createCalendarToggleControl } from "./filters.js";

/**
 * SFC-specific calendar entry point.
 *
 * Wires the generic calendar core to the SFC page: the calendar toggle pills
 * (#calendar-filters) via the built-in control, the category multi-select
 * (#category-select, a Select2 widget) registered as a filter control, and the
 * Slovenian locale. The category->calendar coupling flows one way: toggling a
 * calendar hides the categories of unselected calendars from the Select2 (see
 * syncCategoryOptions) and, through the category predicate, their events.
 */
export function initCalendar(el) {
  var categorySelect = document.getElementById("category-select");
  var calendarFilters = document.getElementById("calendar-filters");

  // Which categories the user has selected
  var activeFilters = new Set();
  // All category IDs (for default detection)
  var allCategoryIds = new Set();
  // category ID -> calendar ID lookup
  var catToCalendar = {};
  // Every category <option>, kept in original order so we can detach/reattach
  // options as their calendar is toggled off/on without losing them.
  var allCategoryOptions = [];

  // Recompute activeFilters from the select's currently-attached selection.
  // Detached options (calendar toggled off) drop out automatically.
  function readActiveFilters() {
    activeFilters.clear();
    if (!categorySelect) return;
    Array.from(categorySelect.selectedOptions).forEach(function (option) {
      activeFilters.add(option.value);
    });
  }

  if (categorySelect) {
    allCategoryOptions = Array.prototype.slice.call(categorySelect.options);
    allCategoryOptions.forEach(function (option) {
      allCategoryIds.add(option.value);
      var calId = option.getAttribute("data-calendar-id");
      if (calId) {
        catToCalendar[option.value] = calId;
      }
    });
    readActiveFilters();
  }

  var controllerRef = null;

  // Which calendars were active at the last sync. Diffed against the current
  // selection to spot off->on transitions so we can auto-select the newly
  // enabled calendar's categories.
  var prevActiveCalendars = new Set();

  // Snapshot (copy, not alias) of the currently active calendars. The copy is
  // required: state.active is the toggle control's live Set, mutated in place
  // on every toggle, so a stored reference would always equal the next read and
  // the off->on diff would never fire.
  function activeCalendarSet() {
    var state = controllerRef && controllerRef.getFilterState("calendars");
    return state ? new Set(state.active) : new Set();
  }

  function isCategoryVisible(catId) {
    var calId = catToCalendar[catId];
    if (!calId) return true;
    var state = controllerRef && controllerRef.getFilterState("calendars");
    return !state || state.active.has(calId);
  }

  // Rebuild the category Select2 to only show categories whose calendar is
  // currently selected. Detached options keep their `selected` state (on a
  // fresh load it is restored from the URL; toggling a calendar on selects all
  // its categories - see the onFilterChange handler). Because this constrains
  // activeFilters to visible categories, the event predicate only needs to test
  // activeFilters membership.
  function syncCategoryOptions() {
    if (!categorySelect) return;
    categorySelect.replaceChildren();
    allCategoryOptions.forEach(function (option) {
      if (isCategoryVisible(option.value)) {
        categorySelect.appendChild(option);
      }
    });
    readActiveFilters();
    $(categorySelect).trigger("change.select2");
  }

  var categoryControl = {
    key: "categories",
    predicate: function (e) {
      var catId = e.extendedProps.categoryId;
      if (catId === null || catId === undefined || catId === "") {
        // Category-less sources (tenders) follow their calendar toggle, not the
        // category select.
        var calId = e.extendedProps.calendarId;
        if (!calId) return true;
        var state = controllerRef && controllerRef.getFilterState("calendars");
        return !state || state.active.has(calId);
      }
      return activeFilters.has(String(catId));
    },
    // Serialize from every option's selected state - including options
    // currently detached because their calendar is toggled off - so the URL
    // keeps their selection and re-enabling the calendar restores its events
    // after a reload.
    serialize: function () {
      var selected = new Set();
      allCategoryOptions.forEach(function (option) {
        if (option.selected) {
          selected.add(option.value);
        }
      });
      return serializeSelectedSet(selected, allCategoryIds);
    },
    getState: function () {
      return { active: activeFilters, all: allCategoryIds };
    },
    init: function (ctx) {
      controllerRef = ctx.controller;
      if (!categorySelect) return;
      // Select2 fires jQuery change events
      $(categorySelect).on("change", function () {
        readActiveFilters();
        ctx.triggerChange("categories");
      });
      // Keep the category Select2 in sync with the calendar selection. When a
      // calendar is toggled on, select all of its categories so every one of
      // its events shows without the user picking them in the Select2.
      prevActiveCalendars = activeCalendarSet();
      ctx.controller.onFilterChange(function (changedKey) {
        if (changedKey !== "calendars") return;
        var current = activeCalendarSet();
        allCategoryOptions.forEach(function (option) {
          var calId = catToCalendar[option.value];
          if (calId && current.has(calId) && !prevActiveCalendars.has(calId)) {
            option.selected = true;
          }
        });
        prevActiveCalendars = current;
        syncCategoryOptions();
      });
      syncCategoryOptions();
    },
  };

  var filters = [];
  if (calendarFilters) {
    filters.push(
      createCalendarToggleControl(el, { containerId: "calendar-filters" }),
    );
  }
  filters.push(categoryControl);

  return createCalendar(el, { filters: filters });
}
